import * as React from 'react';
import { useState, useEffect } from 'react';
import { useContent } from "../../Content/cms";
import { useSelector } from 'react-redux';
import { Stats } from "../../Commons/statistics";

// Import types
import { PopulationData } from '../Protocol/types';
import { StoreState } from '../../store';

// Import components
import Section, { SubSection } from "../../Commons/Section";
import DataFrame from '../../Commons/Components/DataFrame';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import { faInfo, faInfoCircle ,faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { IconButton } from "../../Commons/MultipleComponents";
import { AreaChart, ResponsiveContainer, XAxis, YAxis, Area, ReferenceLine } from "recharts";
import { Semaphore } from './semaphore';
import Col from 'react-bootstrap/Col';

// Import CSS
import './feature_extraction.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';


/*************** Data renders *********************/

interface Histogram { x: number[], y: number[] }

export interface Population {
    name: string
    feature_name: string
    fill_color: string
    stroke_color: string
    histogram: Histogram
}

export interface SemaphoreProbability {
    population: string
    score: string
    value: number
}

export interface FeatureExtractionResScoresData {
    name: string
    status: "pending" | "failed" | "ready"
    value?: number
    semaphore_value: SemaphoreProbability[]
}

interface AreaData {
    x: number,
    [population: string]: number
}

interface FeatureExtractionRowData {
    name: string
    status: "pending" | "failed" | "ready"
    value?: number
    semaphore: SemaphoreProbability[]
    distribution: AreaData[]
    populations: Population[]
}

export interface FeatureExtractionSummaryProps {
    loadingPopulations: boolean
    data: FeatureExtractionResScoresData[]
    populations: Population[] // TODO: rename to PopulationDatas

}

interface ShowPopulation {
    [population: string]: {
        shown: boolean,
        fill_color: string,
        stroke_color: string
    }
};

/****************************************************/


export const FeatureExtractionSummary: React.FC<FeatureExtractionSummaryProps> = (props) => {

    let getContent = useContent();
    const mobiles = require('is-mobile');
    let mobile = mobiles();

    let populations = useSelector<StoreState, PopulationData[]>(state => state.data.populations);
    const isSemaphoreEnabled = useSelector<StoreState, boolean | undefined>(state => state.featureToggle.semaphoreColumn);

    let [info, setInfo] = useState<string | undefined>();
    let [showSemaphoreMetricExplanation, setShowSemaphoreMetricExplanation] = useState<boolean>(false)
    let [tableData, setTableData] = useState<FeatureExtractionRowData[]>([]);
    let [activePopulation, setActivePopulation] = useState<PopulationData>();

    const setInitialActivePopulation = (populations: PopulationData[]) => {

        const controlPopulation = populations.find(pop => pop.name === 'control');
        if (controlPopulation !== undefined) {
            setActivePopulation(controlPopulation);
            return;
        } 

        const generalPopulation = populations.find(pop => pop.name === 'general');
        if (generalPopulation) {
            setActivePopulation(generalPopulation);
            return;
        }

        setActivePopulation(populations[0]);
    };

    useEffect(
        () => {

            setInitialActivePopulation(populations);

            let newTableData: FeatureExtractionRowData[] = [];

            for (const value of Object.values(props.data)) {

                let distribution: AreaData[] = [];

                let populations = props.populations.filter(population => population.feature_name === value.name);

                if (value.status === 'ready') {

                    // add the population distributions
                    for (let population of populations) {

                        let y = 'y_' + population.name;

                        // Add an initial value with value zero to smooth the curve.
                        // The value is added at the start of the valid range sent to not overpass the data x range
                        distribution.push({
                            x: population.histogram.x[0],
                            [y]: 0
                        });

                        // Add the curve values using the center of the X bins as x points.
                        for (let i = 0; i < population.histogram.x.length - 1; i++) {
                            distribution.push({
                                x: Stats.mean(population.histogram.x.slice(i, i + 2)),
                                [y]: population.histogram.y[i]!
                            });
                        }

                        // Add a last value  zero to smooth the curve.
                        // The value is added at the end of the valid range sent to not overpass the data x range
                        distribution.push({
                            x: population.histogram.x[population.histogram.x.length - 1],
                            y: 0
                        });
                    }
                }

                newTableData.push({
                    name: value.name,
                    status: value.status,
                    value: value.value,
                    semaphore: value.semaphore_value,
                    distribution: distribution,
                    populations: populations
                });
            }

            setTableData(newTableData);
        }, [props.data, props.populations, populations] // eslint-disable-line react-hooks/exhaustive-deps
    );

    function toggleShownPopulation(selectedPopulation: string) {
        setActivePopulation(
            populations.find(pop => pop.name === selectedPopulation)
        );
    }


    const header_content = mobile === false ? [
        ...(isSemaphoreEnabled ? [
            <div className='d-flex align-items-center '>
                {getContent("protocol_result__feature_extraction__sumary_table__header__match_population")}
                <FontAwesomeIcon
                    icon={faInfoCircle}
                    color="#8037c7"
                    onClick={() => setShowSemaphoreMetricExplanation(true)}
                    className='ml-2'/>
            </div>
        ] : []),
        getContent("protocol_result__feature_extraction__sumary_table__header__metric"),
        getContent("protocol_result__feature_extraction__sumary_table__header__current_value"),
        getContent("protocol_result__feature_extraction__sumary_table__header__population_distribution"),
        getContent("protocol_result__feature_extraction__sumary_table__header__description"),
    ] : [
        getContent("protocol_result__feature_extraction__sumary_table__header__metric"),
        getContent("protocol_result__feature_extraction__sumary_table__header__current_value"),
        ''
    ];

    const header = header_content.map(col => <p className={'font-weight-bold text-center'}>{col}</p>);


    const sub_header = mobile === false ? [
        <Row className='text-center bg-primary-light w-100 p-0 align-items-center'>
            <Col xs={'auto'} className={'py-1'}>
                <Form.Group className={'mb-0'}>
                    <Form.Control
                        as="select"
                        className="form-control-sm primary-form-control"
                        value={activePopulation?.name || ''}
                        onChange={(e) => toggleShownPopulation(e.target.value)}
                    >
                        {populations.map(population => (
                            <option key={population.name} value={population.name}>
                                {population.name}
                            </option>
                        ))}
                    </Form.Control>
                </Form.Group>
            </Col>
            <Col>
                {props.loadingPopulations && <Spinner animation={'border'} size={'sm'} />}
            </Col>
        </Row>
    ] : undefined;

    let fullSerie = [];

    for (const value of tableData) {

        let value_str = null;
        let distribution = null;

        // Check if distribution data exists and has valid data points for ready status
        const hasValidDistribution = value.status === 'ready' && 
            value.distribution && value.distribution.length > 0 && 
            activePopulation && value.distribution.some(point => point[`y_${activePopulation?.name}`] !== undefined);

        // Skip this row entirely if it's ready status but missing distribution data
        if (value.status === 'ready' && !hasValidDistribution) {
            continue;
        }

        if (value.status === 'pending') {

            value_str = <div className={'text-center'}><Spinner animation={'border'} size={'sm'} /></div>;
            distribution = <div className={'text-center'}><Spinner animation={'border'} size={'sm'} /></div>;

        } else if (value.status === 'failed') {

            value_str = <Alert variant={'danger'}>{getContent('protocol_result__feature_extraction__error')}</Alert>;
            distribution = <Alert variant={'danger'}>{getContent('protocol_result__feature_extraction__error')}</Alert>;

        } else if (value.status === 'ready') {

            if (value.value !== undefined) {

            }

            if (value.value === undefined) {
                throw new Error('value_str is undefined');
            } else {

                const abs = Math.abs(value.value);
                if (abs === 0) {
                    value_str = '0.00';
                } else if (abs >= 0.01) {
                    value_str = value.value.toFixed(2);
                } else if (abs >= 0.001) {
                    value_str = value.value.toFixed(4);
                } else {
                    value_str = value.value.toFixed(6);
                }

                value_str = (
                    <p className={'text-center feature-value'}>{value_str}</p>
                );
            }

            distribution = (
                <ResponsiveContainer width={'100%'} height={72}>
                    <AreaChart data={value.distribution} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                        <XAxis
                            dataKey={'x'}
                            domain={['dataMin', 'dataMax']}
                            type={'number'}
                            tickFormatter={(x) => x.toFixed(1)}
                            interval={'preserveStartEnd'}
                            tick={{ fontSize: 9, fill: '#9ba8c0' }}
                            axisLine={false}
                            tickLine={false}
                        />

                        <YAxis type={'number'} hide />

                        {activePopulation && <Area
                            key={'area_' + activePopulation.name}
                            type={"monotone"}
                            dataKey={"y_" + activePopulation.name}
                            stroke={activePopulation.stroke_color}
                            fill={activePopulation.fill_color}
                            strokeWidth={1.5}
                            isAnimationActive={false}
                        />}

                        <ReferenceLine
                            x={value.value}
                            stroke={'#ba1c1a'}
                            strokeWidth={1.5}
                            strokeDasharray="3 2"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            );
        }

        let explantion =
            <div>
                <small className="pb-2 text-secondary">
                        {getContent(`protocol_result__feature_extraction__${value.name}__short_explanation`)}
                </small>
                <small onClick={() => setInfo(value.name)} className='d-block cursor mt-2'>
                    <FontAwesomeIcon
                        icon={faPlusCircle}
                        color="#8037c7"
                        className="mr-2"
                    ></FontAwesomeIcon>
                    {getContent('protocol_result__feature_extraction__show_more_metric_info')}
                </small>
            </div>
            
        fullSerie.push(mobile === false ? [
            ...(isSemaphoreEnabled ? [<Semaphore value={value.semaphore?.find(s => s.population === activePopulation?.name)?.value || null} population={activePopulation?.name}/>] : []),
            <p className={'mt-2'}>{getContent("protocol_result__feature_extraction__" + value.name + "__name")}</p>,
            value_str,
            distribution,
            explantion,
        ] : [
            ...(isSemaphoreEnabled ? [<Semaphore value={null} population={activePopulation?.name}/>] : []), // semaphore column placeholder for mobile
            <p className={'mt-2'}>{getContent("protocol_result__feature_extraction__" + value.name + "__name")}</p>,
            value_str,
            <IconButton
                text={getContent('protocol_result__feature_extraction__show_more_metric_info')}
                icon={{ icon: faInfo, color: "#8037c7" }}
                onClick={() => setInfo(value.name)}
                className={'text-center w-100'}
            />
        ]);
    }

    // Find divider index based on the original data structure
    // Divider rows are created when value.semaphore is null
    let dividerIdx = fullSerie.findIndex((row, index) => {
        // Get the corresponding data item for this row
        const dataItem = tableData[index];
        if (!dataItem) return true;
        
        // Check if this is a divider row (semaphore is null)
        return dataItem.semaphore.find(s => s.population === activePopulation?.name) === undefined;
    });
    dividerIdx = dividerIdx === -1 ? fullSerie.length : dividerIdx;

    let fullMetricExplanationModal = 
        <Modal show={info !== undefined} onHide={() => setInfo(undefined)} size="lg">
            <Modal.Header closeButton >
                <Modal.Title>
                    {info !== undefined && getContent("protocol_result__feature_extraction__" + info + "__name")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p><b>{getContent('protocol_result__measure_explanation__title__brief')}</b></p>
                <p>{info !== undefined && getContent("protocol_result__feature_extraction__" + info + "__short_explanation")} </p>
                <p><b>{getContent('protocol_result__measure_explanation__title__long')}</b></p>
                {info !== undefined && <div dangerouslySetInnerHTML={{ __html: getContent("protocol_result__feature_extraction__" + info + "__large_explanation") as string }} />}
                <p><b>{getContent('protocol_result__measure_explanation__title__references')}</b></p>
                {info !== undefined &&
                    <div dangerouslySetInnerHTML={{ __html: getContent("protocol_result__feature_extraction__" + info + "__references") as string }} />}
            </Modal.Body>
        </Modal>;

    let semaphoreMetricExplanationModal =
        <Modal show={showSemaphoreMetricExplanation} onHide={() => setShowSemaphoreMetricExplanation(false)} size="lg">
            <Modal.Header closeButton >
                <Modal.Title>
                    { getContent("protocol_result__feature_extraction__Bhattacharyya_distance__modal_title")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p><b>{getContent('protocol_result__measure_explanation__Bhattacharyya_distance__title')}</b></p>
                <p >{getContent("protocol_result__feature_extraction__Bhattacharyya_distance__explanation")} </p>
                {<div dangerouslySetInnerHTML={{ __html: getContent("protocol_result__feature_extraction__Bhattacharyya_distance__references") as string }} />}
            </Modal.Body>
        </Modal>;

    return (
        <Section
            title={getContent("protocol_result__feature_extraction__title")}
            className={'secondary-section'}
            headerClassName={'secondary-section-header'}
            bodyClassName={'secondary-section-body'}
        >

            {/* feature extraction table */}
            <SubSection title={''} className={'secondary-subsection '}>
                <DataFrame
                    header={header}
                    subheader={sub_header}
                    dataframe={fullSerie}
                    striped={false}
                    className={'header-bordered mt-3 w-100'}
                    columnClassName={'text-center'}
                    dividerIndex = {dividerIdx}
                    dividerText={getContent("protocol_result__semaphore__divider_text")} 
                    pageSize={fullSerie.length + 1}
                    sortableColumns={isSemaphoreEnabled ? [1, 2] : [0, 1]}
                    columnWidths={isSemaphoreEnabled ? [15, 20, 12, 16, 37] : [20, 12, 16, 52]}
                />
            </SubSection>
            {fullMetricExplanationModal}
            {isSemaphoreEnabled && semaphoreMetricExplanationModal}
        </Section>
    );
};
